<template>
  <Teleport to="#AppContainer">
    <transition name="modal">
      <UIModalOverlay
        v-if="isOpen"
        key="modalOverlay"
        :class="overlayClass"
        :size="size"
        @mousedown.self="closeOnClickAway"
        @keypress.enter="$emit('enter')"
      >
        <div
          :class="['UIModal', `UIModal--${size}`, $attrs.class]"
          v-bind="$attrs"
        >
          <slot name="container">
            <slot name="header">
              <UIModalHeader
                v-if="$slots['title']"
                :showClose="showClose"
                @close="$emit('close')"
              >
                <slot name="title" />
              </UIModalHeader>
            </slot>

            <slot name="content-container">
              <UIModalContent
                v-if="$slots['content']"
                :noBottomPadding="!!$slots['actions']"
              >
                <slot name="content" />
              </UIModalContent>

              <slot name="actions-continer">
                <UIModalActions v-if="$slots['actions']">
                  <slot name="actions" />
                </UIModalActions>
              </slot>
            </slot>
          </slot>
        </div>
      </UIModalOverlay>
    </transition>
  </Teleport>
</template>

<script lang="ts" setup>
import { ref } from 'vue'

import { watchOpenOverlay } from '@/composables/useOpenOverlay'
import { useOverlayFocus } from '@/composables/useOverlayFocus'

import UIModalActions from './UIModalActions.vue'
import UIModalContent from './UIModalContent.vue'
import UIModalHeader from './UIModalHeader.vue'
import UIModalOverlay from './UIModalOverlay.vue'

import type { IUIModalSizes } from './types'

defineOptions({
  inheritAttrs: false,
})
const props = withDefaults(
  defineProps<{
    isOpen?: boolean
    size?: IUIModalSizes
    clickAwayClose?: boolean
    showClose?: boolean
    overlayClass?: string
  }>(),
  {
    size: 'md',
    clickAwayClose: false,
    showClose: true,
    overlayClass: '',
    padding: 'md',
  },
)
const emits = defineEmits(['close', 'enter'])

watchOpenOverlay(
  () => !!props.isOpen,
  (e) => {
    if (props.clickAwayClose) {
      e.preventDefault()
      emits('close')
    }
  },
)

// Focus management for accessibility
const modalOverlay = ref<InstanceType<typeof UIModalOverlay> | null>(null)
useOverlayFocus(modalOverlay, () => props.isOpen)

const closeOnClickAway = () => {
  if (props.clickAwayClose) {
    emits('close')
  }
}
</script>

<style lang="scss">
$_width-sm: 448px;
$_width-md: 29.5rem;
// $_width-md: 33.75rem;
$_width-lg: 46.875rem;
$_width-xl: 80rem;
$_width-full: 59.375rem;

.UIModal {
  @include trans(0.1s);
  @include customScrollbar;
  --padding-header: 1rem;
  --padding-content: 0 1rem;
  --padding-actions: 1rem;
  width: var(--modal-size);
  background: $color-white;
  color: $color-neutral-170;
  border-radius: 8px;
  margin: 0 1rem;
  max-height: 90%;
  display: flex;
  flex-direction: column;
  outline: none; /* Remove focus outline if desired */

  &.UIModal--sm {
    --modal-size: #{$_width-sm};
  }
  &.UIModal--md {
    --modal-size: #{$_width-md};
  }
  &.UIModal--lg {
    --modal-size: #{$_width-lg};
  }
  &.UIModal--xl {
    --modal-size: #{$_width-xl};
  }
  &.UIModal--full {
    --modal-size: #{$_width-xl};
  }
  &.UIModal--auto {
    --modal-size: auto;
  }
}

.modal-enter-from {
  opacity: 0;
}

.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .UIModal,
.modal-leave-to .UIModal {
  -webkit-transform: scale(1.1);
  transform: scale(1.1);
}
</style>
